/* eslint-disable react-hooks/exhaustive-deps */
import {
  Checkbox,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { FC, useMemo, useEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useAppSelector } from 'src/app/hooks';
import ChipSocialMediaObjectSelector from 'src/components/ChipSocialMediaObjectSelector';
import Empty from 'src/components/Empty';
import { IObject } from 'src/models/objectManagement';
import SearchIcon from '@mui/icons-material/Search';
import { useFiltersByName } from 'src/hooks/useFiltersByName';
import { useUnlimitedScroll } from 'src/hooks/useUnlimitedScroll';
import { useSelectItemDefaultSelected } from 'src/hooks/useSelectItemDefaultSelected';
import ModalAddSocialProfile from 'src/components/ModalAddSocialProfile';
import { Box } from '@mui/system';
import FilterNoneIcon from '@mui/icons-material/FilterNone';
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { useLocation } from 'react-router';

interface Props {
  objectList: Array<IObject>;
  objectListIdActive: Array<string>;
  onParentVisible?: () => void;
  onSelect: (items: Array<string>) => void;
}

interface Item {
  name: string;
}

const SectionSocialProfile: FC<Props> = ({
  objectList,
  onParentVisible = () => {},
  onSelect,
  objectListIdActive
}) => {
  const theme = useTheme();
  const { pathname, search } = useLocation();
  const params = new URLSearchParams(search);
  const tab = params.get('tab');
  const isEcommerce = pathname.includes('ecommerce') || tab === 'ecommerce';

  const { groupSelectedById } = useAppSelector(
    (state) => state.objectManagement
  );
  const { isOnTour } = useAppSelector((state) => state.storeProductTour);

  const addName = useMemo((): Array<IObject & Item> => {
    return objectList.map((object) => ({
      ...object,
      name: object.objectName
    }));
  }, [objectList]);

  const { filteredList: unsortedFilteredList, handleFilter } =
    useFiltersByName<IObject>(addName);

  const filteredList = useMemo(() => {
    if (!objectListIdActive.length) return unsortedFilteredList;
    if (!unsortedFilteredList.length) return [];

    const activeIdsSet = new Set(objectListIdActive);
    return unsortedFilteredList.sort((a, b) => {
      const aIsActive = activeIdsSet.has(a.id);
      const bIsActive = activeIdsSet.has(b.id);
      if (aIsActive && !bIsActive) return -1;
      if (!aIsActive && bIsActive) return 1;
      return 0;
    });
  }, [unsortedFilteredList]); // add objectListIdActive as dependency if something went wrong

  const { fetchMoreData, infiniteScroll } = useUnlimitedScroll<IObject>(
    isOnTour ? filteredList.slice(0, 3) : filteredList
  );

  const { selectedItemByIds, handleClick, isSelected, handleSelectAll } =
    useSelectItemDefaultSelected<IObject>(filteredList, objectListIdActive);

  const { isCheckedAll, isIndeterminate } = useMemo(() => {
    const selectedIdsSet = new Set(selectedItemByIds);
    const checkedItemsCount = filteredList.filter((el) =>
      selectedIdsSet.has(el.id)
    ).length;
    const isCheckedAll = filteredList.length === checkedItemsCount;
    const isIndeterminate =
      checkedItemsCount > 0 && checkedItemsCount < filteredList.length;

    return { isCheckedAll, isIndeterminate };
  }, [filteredList, selectedItemByIds]);

  useEffect(() => {
    onSelect(selectedItemByIds);
  }, [selectedItemByIds]);

  return (
    <>
      <DialogTitle
        sx={{
          m: theme.spacing(3, 3, 1),
          p: theme.spacing(2, 3, 0, 3)
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h3">
            {isEcommerce ? 'Store Account' : 'Social Profile'}
          </Typography>
          {groupSelectedById === '_default' ? (
            <ModalAddSocialProfile
              title="Social Profile"
              onHide={onParentVisible}
              objectList={objectList}
            />
          ) : null}
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={{
          m: theme.spacing(1, 3)
        }}
      >
        <Stack direction="row" spacing={2} sx={{ mb: theme.spacing(1) }}>
          <Box sx={{ minWidth: theme.spacing(15) }}>
            <FormControlLabel
              control={
                <Checkbox
                  icon={<FilterNoneIcon />}
                  checkedIcon={<LibraryAddCheckIcon />}
                  indeterminateIcon={<IndeterminateCheckBoxIcon />}
                  indeterminate={isIndeterminate}
                />
              }
              label="Select All"
              onChange={handleSelectAll}
              checked={isCheckedAll}
            />
          </Box>
          <TextField
            fullWidth
            id="standard-basic"
            placeholder="Search Profile"
            variant="standard"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{
              mb: theme.spacing(3)
            }}
            onChange={(e) => handleFilter(e.target.value)}
          />
        </Stack>
        <InfiniteScroll
          dataLength={infiniteScroll.list.length}
          next={fetchMoreData}
          hasMore={infiniteScroll.hasMore}
          loader={<div />}
          height={104}
          endMessage={<div />}
        >
          {infiniteScroll.list.length > 0 ? (
            infiniteScroll.list.map(
              ({
                id,
                content,
                objectName,
                socialMedia,
                isHashtag,
                streamType
              }) => {
                const isItemSelected = !isSelected(id);
                return (
                  <ChipSocialMediaObjectSelector
                    key={`${id}${content}`}
                    id={content}
                    name={objectName}
                    socialMedia={socialMedia}
                    streamType={isHashtag ? 'hashtag' : streamType}
                    sx={{ m: '0 8px 8px 0' }}
                    onClick={(event) => handleClick(event, id)}
                    checked={isItemSelected}
                  />
                );
              }
            )
          ) : (
            <Empty />
          )}
        </InfiniteScroll>
      </DialogContent>
    </>
  );
};

export default SectionSocialProfile;
